/**
 * Single Source of Truth for Proficiency Categories
 * 
 * IMPORTANT: All category definitions are imported from canonicalCategories.json
 * This ensures identical mappings between frontend and edge functions.
 */

import canonicalData from './canonicalCategories.json';

// Re-export the canonical data with proper typing
export const PROFICIENCY_CATEGORIES = canonicalData.proficiencyCategories as readonly string[];

export type ProficiencyCategory = typeof canonicalData.proficiencyCategories[number];

/**
 * Mapping from jobs_catalog category_key to proficiency category display name
 * This ensures exact matching between job categories and technician proficiencies
 */
export const CATEGORY_KEY_TO_PROFICIENCY: Record<string, ProficiencyCategory> = 
  canonicalData.categoryKeyToProficiency as Record<string, ProficiencyCategory>;

/**
 * Proficiency category weights for skill tier calculation
 * Higher weight = more impact on overall skill tier
 */
export const PROFICIENCY_WEIGHTS: Record<string, number> = canonicalData.proficiencyWeights;

/**
 * Map proficiency categories to skill tiers (1-10 scale)
 * This defines the inherent complexity/skill level required for each category
 */
export const CATEGORY_SKILL_TIER: Record<string, number> = canonicalData.categorySkillTiers;

/**
 * Default fallback category when input cannot be mapped
 */
export const DEFAULT_FALLBACK_CATEGORY = canonicalData.defaultFallbackCategory as ProficiencyCategory;

/**
 * Proficiency categories that are NOT mechanical skill (today: Detailing).
 *
 * They are real proficiencies — learned from completed work, shown on the
 * Passport, matched by Smart Assign — but they say nothing about mechanical
 * capability, so they are left out of the mechanical skill-tier average. The
 * same doctrine keeps detail work from ever counting toward a mechanical
 * category in CORE-E learning.
 */
export const NON_MECHANICAL_PROFICIENCY_CATEGORIES: readonly string[] =
  (canonicalData as { nonMechanicalProficiencyCategories?: string[] }).nonMechanicalProficiencyCategories ?? [];

export function isMechanicalProficiencyCategory(category: string): boolean {
  return !NON_MECHANICAL_PROFICIENCY_CATEGORIES.includes(category);
}

/**
 * Calculate overall skill tier from proficiencies
 * @param proficiencies - Record of category name to proficiency score (0-100)
 * @returns Calculated skill tier (1-10)
 */
export function calculateSkillTier(proficiencies: Record<string, number>): number {
  let weightedSum = 0;
  let totalWeight = 0;

  // The MECHANICAL skill tier: a Detailing score neither raises nor dilutes it.
  PROFICIENCY_CATEGORIES.filter(isMechanicalProficiencyCategory).forEach(category => {
    const proficiency = proficiencies[category] || 0;
    const weight = PROFICIENCY_WEIGHTS[category] || 0.5;
    weightedSum += proficiency * weight;
    totalWeight += weight;
  });

  const weightedAvg = totalWeight > 0 ? weightedSum / totalWeight : 0;

  // Map weighted average to 10-tier system
  if (weightedAvg >= 91) return 10;
  if (weightedAvg >= 81) return 9;
  if (weightedAvg >= 71) return 8;
  if (weightedAvg >= 61) return 7;
  if (weightedAvg >= 51) return 6;
  if (weightedAvg >= 41) return 5;
  if (weightedAvg >= 31) return 4;
  if (weightedAvg >= 21) return 3;
  if (weightedAvg >= 11) return 2;
  return 1;
}

/**
 * Resolve the skill tier to DISPLAY/PERSIST for a technician.
 *
 * Derived-from-proficiencies is the more accurate signal once real
 * proficiency data exists, but a technician created through the manual
 * setup wizard has `proficiencies = {}` — for those rows the operator's
 * stored `technicians.skill_tier` is the ONLY record of their tier, and
 * calculateSkillTier() would return the floor (1).
 *
 * Every surface that renders or writes a skill tier must go through this
 * resolver so the roster cards, the edit form, and the save payload can
 * never disagree. Previously TechnicianForm recomputed unconditionally on
 * load, so opening the form on a wizard-created tech showed tier 1 and any
 * subsequent save persisted that 1 over the stored tier (silent downgrade).
 *
 * @param proficiencies - Record of category name to proficiency score (0-100)
 * @param storedTier - The persisted technicians.skill_tier, if known
 * @returns Skill tier (1-10)
 */
export function resolveSkillTier(
  proficiencies: Record<string, number> | null | undefined,
  storedTier?: number | null,
): number {
  const prof = proficiencies || {};
  const hasProficiencyData = Object.values(prof).length > 0;
  if (hasProficiencyData) return calculateSkillTier(prof);
  return storedTier ?? 1;
}

/**
 * Get proficiency category from job category_key
 * @param categoryKey - The category_key from jobs_catalog
 * @returns The proficiency category display name, or 'Maintenance' as fallback
 */
export function getProficiencyCategory(input: string): ProficiencyCategory {
  const raw = (input ?? '').trim();
  if (!raw) return DEFAULT_FALLBACK_CATEGORY;

  // ALREADY CANONICAL. This case was missing, and it is the one that broke the
  // proficiency loop: a job carrying 'Brakes' — a perfectly correct
  // proficiency identity — found no entry in a map keyed by catalogue keys and
  // fell through to Maintenance. So the scheduler read a category the
  // technician had never worked, and demonstrated capability in the category
  // they HAD worked was invisible. Resolution must be idempotent:
  //   brakes -> Brakes   and   Brakes -> Brakes
  if (isValidProficiencyCategory(raw)) return raw;

  // A catalogue key.
  const mapped = CATEGORY_KEY_TO_PROFICIENCY[raw];
  if (mapped) return mapped;

  // Tolerate case on either form. `heatingVentilationAC` is camelCase in the
  // canonical contract, so a lower-cased comparison is not enough on its own.
  // Taken from `input`, not from `raw`: `ProficiencyCategory` resolves to
  // plain `string`, so the type guard above narrows `raw` to `never` on this
  // side of it.
  const lowered = (input ?? '').trim().toLowerCase();
  const key = Object.keys(CATEGORY_KEY_TO_PROFICIENCY)
    .find((k) => k.toLowerCase() === lowered);
  if (key) return CATEGORY_KEY_TO_PROFICIENCY[key];
  const name = (PROFICIENCY_CATEGORIES as readonly string[])
    .find((c) => c.toLowerCase() === lowered);
  if (name) return name as ProficiencyCategory;

  return DEFAULT_FALLBACK_CATEGORY;
}

/**
 * Validate if a string is a valid proficiency category
 */
export function isValidProficiencyCategory(category: string): category is ProficiencyCategory {
  return PROFICIENCY_CATEGORIES.includes(category);
}

/**
 * Get skill tier for a job category
 * @param categoryKey - The category_key from jobs_catalog
 * @returns The skill tier (1-10) for this category
 */
export function getCategorySkillTier(categoryKey: string): number {
  const proficiencyCategory = getProficiencyCategory(categoryKey);
  return CATEGORY_SKILL_TIER[proficiencyCategory] || 3; // Default to Tier 3 if unknown
}
